// Where a stored USD figure CAME FROM — the discriminator that stops a ceiling
// being read as a price.
//
// THE DEFECT THIS EXISTS FOR. `agent_sessions.cost_usd` was `numeric(12,6) NOT
// NULL DEFAULT '0'`: one number, no provenance. Three different facts collapsed
// onto the value `0` — "this run was free", "this run has not reported yet", and
// "we could not price this model" — and a fourth, "this number is a fail-safe
// CEILING we invented so a cap would trip early", was indistinguishable from a
// measured price. Measured on prod 2026-08-26: 8,309 `minimax:MiniMax-M2`
// sessions carrying $1,835.93 that reproduces the `FALLBACK_BY_PROVIDER` minimax
// ceiling to the cent, and 7,130 fleet sessions showing $0.00 over 33.5M tokens
// because `priceRunUsage` correctly refused to guess and the NOT-NULL default
// overwrote its `null` with zero.
//
// A number a customer sees must be traceable to a source. That is not achievable
// with a nullable column plus a convention, because a convention has no seam to
// enforce it and a DEFAULT can always fill the hole back in — the fleet writer's
// own doc comment established the convention "tokens > 0 AND cost_usd = 0 means
// UNPRICED", and nothing in the schema could hold it. So the basis rides WITH the
// figure on the same row, under a CHECK constraint, and `unpriced` is spelled as
// a NULL cost the constraint requires rather than as a zero anybody can mistake.

import { z } from 'zod';

/**
 * How a session row's `cost_usd` was derived.
 *
 * Ordered here weakest-claim-last on purpose; {@link weakestPriceBasis} folds a
 * run whose dollars came from more than one source down to the least trustworthy
 * contributor, because a total containing one estimate IS an estimate.
 */
export const PriceBasis = {
  /**
   * The PROVIDER stated what every call actually cost
   * (`providerMetadata.openrouter.usage.cost`, #1461), and `cost_usd` is the sum
   * of those reports verbatim. The strongest claim available: a measurement, not
   * an estimate — no registry can track what a router actually charged (cache
   * discounts, per-endpoint pricing).
   *
   * Written only when the session was FULLY reported: `fullyProviderReported`
   * (`@developerz/agent`, `loop/usage.ts`) holds when the ledger's reported bucket
   * is the whole session — `reportedTokenIn === tokenIn`, `reportedTokenOut ===
   * tokenOut`, and not empty — and rides out on `RunSessionResult`. A legitimate
   * $0 report counts; the provider vouched the call was free. A FLEET session
   * gets it by the same rule one level up (2026-11-03): a box `usage` row carries
   * `cost_usd` only when every call it covers reported one, and the worker's fold
   * (`apps/worker/src/fleet/usage-ingest.ts`) writes this basis only when every
   * row of the run did.
   *
   * A MIXED session is not this. `CostSource` is `'provider'` once ANY call
   * reported, and a total whose other calls self-priced is part measurement, part
   * estimate or ceiling — so it grades on its self-priced share, which understates
   * trust and never overstates it. That distinction keeps its durable home on the
   * terminal audit row (`costSource`, #1461): `costSource: 'provider'` beside a
   * self-priced basis (`registry`, `provider_rates`, `provider_fallback`) is the
   * mixed case; a flat-rate ledger books no reported dollars (#775). Before this
   * member, a fully
   * reported session on a model `MODEL_REGISTRY` missed was stamped
   * `provider_fallback`, so the row could not say whether its dollars were a
   * report or a ceiling (#3937, filed 2026-09-14: 153 `openrouter:z-ai/glm-5.3`
   * sessions, $22.16, arithmetically not the ceiling).
   */
  ProviderReport: 'provider_report',
  /**
   * Priced from a `MODEL_REGISTRY` row (`@developerz/config`): published list
   * price x reported tokens. An ESTIMATE of the customer's bill, but one every
   * figure in it is sourced and version-controlled.
   */
  Registry: 'registry',
  /**
   * Priced from the rate card the PROVIDER published for this exact model
   * (`byok_key_models.input_per_mtok` / `output_per_mtok`, arc 101 slice 02):
   * that card x reported tokens.
   *
   * The same ARITHMETIC as {@link PriceBasis.Registry} and a strictly stronger
   * SOURCE, which is why it is a member rather than a reuse of that label.
   * `registry` names a `MODEL_REGISTRY` row a person wrote down and version
   * controlled; this names a number read off the `/models` response of the
   * endpoint the key actually dials, captured with the key. Calling both
   * `registry` would make the provenance column say a thing that is not true of
   * half the rows it labels — the one failure this vocabulary exists to prevent.
   *
   * DETECTED BEATS DECLARED, and prod says why: of 10,195 sessions with real
   * tokens on 2026-08-27, 699 carried an OpenRouter reference (6.9%) totalling
   * $21.00 against $2,032.15 billed, and `MODEL_REGISTRY` carries no row at all
   * for most ids prod runs. A hand-curated table ages from the day it is written;
   * this one cannot drift from the endpoint, because it came from it.
   *
   * NOT the same claim as {@link PriceBasis.ProviderReport}. That is the
   * provider stating what a CALL cost; this is the provider stating its LIST
   * PRICE, which we then multiply by our own token counts. It is an estimate,
   * like `registry` — a better-sourced one.
   */
  ProviderRates: 'provider_rates',
  /**
   * A flat-rate coding-plan credential (Kimi Code / z.ai coding). $0 per token
   * by contract, not by measurement — the subscription already paid.
   */
  FlatRate: 'flat_rate',
  /**
   * A deliberately-HIGH fallback ceiling from `@developerz/domain`
   * (`value-objects/fallback-price.ts`): the LEDGER's stand-in — the rows of the
   * nine providers it always priced, else the flat $5/$20 catch-all — booked
   * because the (provider, model) pair had no registry row. That stand-in is
   * FROZEN, and the account cap does not read it: the meter prices every row
   * the platform could not SOURCE at the conservative CEILING beside it
   * (`fallbackPriceFor`), which covers every rate the platform holds.
   *
   * **This is not a price and must never be rendered as one.** The ceiling is
   * fail-safe for the question it was built for ("should this session be
   * stopped"), where over-counting is the safe direction; it is a fabricated
   * number for the question a customer asks ("what did this cost"). Surfaces
   * show an upper bound or nothing.
   *
   * **THE METER'S SOURCE ORDER, AND WHY THE CEILING IS ITS LAST RESORT.** The
   * account spend meter (`monthlySpend`, `@developerz/db`) feeds the cap the
   * customer set, so it may not be a fabricated number where a real one exists.
   * What remains a ceiling is returned APART (`MonthlySpend.estimatedUsd`): the
   * Usage panel (`usageSnapshot`) and the spend email
   * (`apps/agents-manager/src/wiring/spend-threshold.ts`) show the priced figure
   * as the amount used and the ceiling on its own line, and only the band reads
   * their sum (`sumMonthlySpendUsd`). The meter also never reads a row booked
   * at THIS basis below its own booked `cost_usd`: the booking is the ledger's
   * record and carries the session's provider-reported dollars verbatim
   * (#1461), so where it exceeds the stored `reference_usd` the booking is the
   * floor — the measured component is retained, never swapped for a list price
   * below it (#7683 review) — and THIS ceiling only ever tops a no-reference row
   * up above that floor, per row. The booking above a list price, and a
   * no-reference row's whole bound, are returned as the ESTIMATE, never as
   * money spent: the row cannot say which of its dollars were reported. An
   * UNPRICED row
   * (`cost_usd IS NULL`, the row's own basis) is therefore metered at its
   * stored `reference_usd` — what those tokens were worth at OpenRouter's
   * published list rates — and at THIS ceiling only for a row the platform can
   * neither price nor value. Measured on prod (read-only, 2026-10-01 to 11-01)
   * at the OLD $5/$20 catch-all, that order was worth 16x: the largest account
   * read $2,956.51 before any ceiling and $63,043.07 with the catch-all, against
   * $3,930.55 of stored `reference_usd` for the same 5,431 unpriced rows — a cap
   * set on the ceiling refused at roughly 1/16 of real spend. The catch-all now
   * sits at the platform's TOP tier ($6/$60), and the same month re-measured
   * reads about $25.9k — $6,902.72 of priced spend plus $19,022.57 of ceiling for
   * the 4,490 unsourced rows that carry no list price — roughly 4x the priced
   * figure.
   *
   * **THE CEILING'S LIMIT IS NOT THE REGISTRY.** It over-counts only against the
   * rates the platform HOLDS; it under-counts any model priced above it, on any
   * provider, REGISTERED OR NOT — and an unpriced row is by construction a model
   * with no registry row. The census of the HELD rates it misses lives where
   * the registry is (`packages/config/src/model-coverage.test.ts`), derived
   * rather than hand-copied, so it goes red when the registry changes.
   *
   * **TWO READERS, OPPOSITE OBLIGATIONS.** The customer-facing rollup
   * (`usageByModelAndBasis` and the fold behind it, and `accountBurn`) reports
   * an unpriced row as `unpriced` and adds nothing to a money total. The meter
   * prices it, because a cap that ignored a session the platform could not
   * price would not be a cap. A row booked AT a ceiling (this basis) keeps its
   * recorded cost in both: that cost is what the ledger booked.
   */
  ProviderFallback: 'provider_fallback',
  /**
   * Tokens were spent and this platform cannot price them. `cost_usd IS NULL`.
   * The honest answer, and the one the old column shape made unrepresentable.
   */
  Unpriced: 'unpriced',
  /**
   * No spend has been recorded on this row: a session born but not yet settled,
   * a synthetic audit-chain parent that runs no model (`gateway` / `human` /
   * `platform` / `rollout`), or a fleet parent whose box has not reported usage.
   * `cost_usd` is 0 and that zero is a placeholder, never a measurement.
   */
  NotRecorded: 'not_recorded',
  /**
   * Written before this column existed. We do not know how the figure was
   * derived and will not guess — the migration that added `price_basis`
   * backfilled every pre-existing row to this value rather than reverse-engineer
   * one, so `SELECT count(*) WHERE price_basis = 'legacy_unattributed'` is
   * exactly the size of the un-auditable backlog (18,581 rows on 2026-08-26).
   *
   * Nothing writes this value. It only ever shrinks.
   */
  LegacyUnattributed: 'legacy_unattributed',
} as const;

export const PriceBasisSchema = z.enum(PriceBasis);
export type PriceBasis = z.infer<typeof PriceBasisSchema>;

/**
 * The bases whose figure is a MEASUREMENT or a sourced list-price estimate —
 * i.e. the only ones a customer-facing surface may render as a cost.
 *
 * Everything else is a placeholder (`not_recorded`), an admission
 * (`unpriced`, `legacy_unattributed`), or a fail-safe ceiling
 * (`provider_fallback`). Expressed as the set to ALLOW rather than the set to
 * deny: a basis added later is refused by default instead of silently
 * inheriting permission to be displayed as money.
 */
export type DisplayablePriceBasis =
  | typeof PriceBasis.ProviderReport
  | typeof PriceBasis.Registry
  | typeof PriceBasis.ProviderRates
  | typeof PriceBasis.FlatRate;

/**
 * A RECORD over the union rather than a `Set` of members, so the allow-list and
 * {@link DisplayablePriceBasis} cannot drift: a member added to the union with no
 * row here fails to compile, and a row here that is not in the union does too.
 * Without that link the type predicate below would be an assertion nobody checks.
 */
const DISPLAYABLE: Readonly<Record<DisplayablePriceBasis, true>> = {
  [PriceBasis.ProviderReport]: true,
  [PriceBasis.Registry]: true,
  [PriceBasis.ProviderRates]: true,
  [PriceBasis.FlatRate]: true,
};

/**
 * May a `cost_usd` on this basis be shown to a customer as a cost?
 *
 * A TYPE PREDICATE, not a `boolean`: the complement is what an aggregate has to
 * enumerate — every basis it excluded from a total needs a stated reason
 * (`usage-rollup.ts`) — and against a bare boolean that enumeration is
 * uncheckable, so a basis added later would silently ship unexplained.
 *
 * `Object.hasOwn`, never `in`: `in` walks the prototype chain, so a runtime value
 * of `'toString'` reaching a raw predicate would answer true.
 */
export function isDisplayableCost(basis: PriceBasis): basis is DisplayablePriceBasis {
  return Object.hasOwn(DISPLAYABLE, basis);
}

/**
 * Least-trustworthy-wins ordering. Lower rank = stronger claim.
 *
 * `provider_report` outranks everything: it is the only MEASUREMENT here — the
 * provider's own statement of what the calls cost. The other estimated bases
 * (`registry`, `provider_rates`) are a list price times our own token counts;
 * `flat_rate` is $0 by contract, a fact rather than a measurement.
 *
 * `provider_rates` outranks `registry` because that is the whole precedence rule
 * the column was added for — DETECTED beats DECLARED. Both are list-price
 * estimates over our own token counts, and they differ only in whether a person
 * or the endpoint supplied the card; the hand-written one is the one that has
 * measurably drifted.
 *
 * `legacy_unattributed` is deliberately the weakest: a row nobody can account
 * for must never be folded into a total that then claims to be sourced.
 */
const RANK: Readonly<Record<PriceBasis, number>> = {
  [PriceBasis.ProviderReport]: 0,
  [PriceBasis.ProviderRates]: 1,
  [PriceBasis.Registry]: 2,
  [PriceBasis.FlatRate]: 3,
  [PriceBasis.NotRecorded]: 4,
  [PriceBasis.ProviderFallback]: 5,
  [PriceBasis.Unpriced]: 6,
  [PriceBasis.LegacyUnattributed]: 7,
};

/**
 * Fold several contributing bases into the one that describes the SUM.
 *
 * A session that self-priced half its tokens off a ceiling and took a provider
 * report for the other half has a total containing a fabricated number, so the
 * total is `provider_fallback`. Averaging trust is the mistake this prevents.
 * Empty input is `not_recorded` — nothing contributed, so nothing is claimed.
 */
export function weakestPriceBasis(bases: readonly PriceBasis[]): PriceBasis {
  // Seeded from the FIRST contributor, never from a fixed member. Seeding from
  // `not_recorded` looked harmless and was wrong in the one direction that
  // matters: every basis stronger than the seed lost to it, so a fold over two
  // real, DISPLAYABLE contributors answered `not_recorded` — a total that had
  // been measured, reported as a placeholder. Caught by
  // `packages/agent/src/price-basis.test.ts`, not by reading.
  const first = bases[0];
  if (first === undefined) return PriceBasis.NotRecorded;
  let worst: PriceBasis = first;
  for (const basis of bases) {
    if (RANK[basis] > RANK[worst]) worst = basis;
  }
  return worst;
}
